import {
  useCallback,
  useEffect,
  useEffectEvent,
  useId,
  useMemo,
  useState,
} from "react";
import dayjs from "dayjs";
import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaSheets } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaSheets";
import styles from "./TaskViewerSheet.module.css";
import UserNameViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserNameViewerFragment/UserNameViewerFragment";
import { PatchTask } from "@coworker/apprestructured/src/task-management/types/Task";
import useSearchParamState from "@coworker/apprestructured/src/shared/hooks/useSearchParamState";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import LoadingViewerTop from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/ViewerTop/LoadingViewerTop/LoadingViewerTop";
import MenuSheetItem from "@coworker/apprestructured/src/task-management/components/MenuSheet/MenuSheetItem/MenuSheetItem";
import { useUID } from "@coworker/app/src/core/auth/useLoggedInUser";
import MenuSheet from "@coworker/apprestructured/src/task-management/components/MenuSheet/MenuSheet";
import CloseSheet, {
  CloseReasonOption,
} from "@coworker/apprestructured/src/task-management/components/CloseSheet/CloseSheet";
import { useTranslation } from "@coworker/locales";
import { TFunction } from "../../../../../packages/locales/translations/util/useTranslation";
import UserAndTeamViewerFragment from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/UserAndTeamViewerFragment/UserAndTeamViewerFragment";
import useSaveGuard from "@coworker/apprestructured/src/save-guard/useSaveGuard";
import useSaveGuardedFunction from "@coworker/apprestructured/src/save-guard/useSaveGuardedFunction";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { NotFound } from "../NotFound/NotFound";
import { useUpdateTask } from "@coworker/apprestructured/src/task-management/services/useUpdateTask";
import { useAllCloseReasons } from "@coworker/apprestructured/src/task-management/services/useAllCloseReasons";
import ViewerFragment, {
  ViewerFragmentCommonProps,
} from "@coworker/apprestructured/src/task-management/components/TaskViewerSheet/viewer-fragments/ViewerFragment";
import { LOADING } from "@coworker/apprestructured/src/task-management/loading";
import { useAllTaskTypes } from "@coworker/apprestructured/src/task-management/services/useTaskTypes";
import { useTask } from "@coworker/apprestructured/src/task-management/services/useTask";
import { merge } from "@coworker/apprestructured/src/shared/helpers/object.helper";
import {
  DEFAULT_TAB,
  LogsAndComments,
  Tab,
} from "../LogsAndComments/LogsAndComments";
import { useTaskLogs } from "../../services/useTaskLogs";
import { Log } from "../../types/Log";
import TitledText from "@coworker/apprestructured/src/task-management/components/TitledText/TitledText";
import { useTaskComments } from "../../services/useTaskComments";
import { Comment } from "../../types/Comment";
import { useCreateComment } from "../../services/useCreateComment";
import { useUser } from "@coworker/apprestructured/src/shared/hooks/users/useUser";
import { Images } from "@coworker/apprestructured/src/task-management/components/Images/Images";
import { useTaskImages } from "@coworker/apprestructured/src/task-management/services/useTaskImages";
import TaskViewerHeader from "./ViewerTop/TaskViewerHeader/TaskViewerHeader";
import { useImageUploader } from "@coworker/apprestructured/src/uploader/useImageUploader";
import ViewerTopFragment, {
  ViewerTopFragmentCommonProps,
} from "./ViewerTop/viewer-top-fragment/ViewerTopFragment";
import {
  getTaskCommentsQueryKey,
  invalidateTaskQueries,
} from "@coworker/apprestructured/src/task-management/services/queryKeyHelper";
import { reportMessageToSentry } from "@coworker/app/src/hooks/useConfigureSentry";
import { useQueryClient } from "@tanstack/react-query";
import { useTaskFiles } from "@coworker/apprestructured/src/task-management/services/useTaskFiles";
import {
  FileItem,
  Files,
} from "@coworker/apprestructured/src/task-management/components/Files/Files";
import { downloadFileFromUrl } from "@coworker/apprestructured/src/shared/helpers/downloadFileFromUrl";
import { formatFileSize } from "@coworker/apprestructured/src/shared/helpers/formatFileSize";
import { useTaskCommentsImages } from "@coworker/apprestructured/src/task-management/services/useTaskCommentsImages";
import { CommentsTabItem } from "@coworker/apprestructured/src/task-management/components/LogsAndComments/CommentsTab/CommentsTab";
import {
  filesToNewFiles,
  NewFile,
} from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { useTaskSchedule } from "../../services/useTaskSchedule";
import SomethingWentWrongPrompt from "@coworker/apprestructured/src/task-management/components/SomethingWentWrongPrompt/SomethingWentWrongPrompt";

const NO_CLOSE_REASON_OPTIONS: CloseReasonOption[] = [];
const NO_COMMENT_IMAGE_URLS: string[] = [];
const NO_FILE_ITEMS: FileItem[] = [];

/**
 * The ViewTaskSheet is open for the task with id 'taskViewer' taken from the url
 * search params. Open/close the sheet by adding/removing a 'taskViewer'
 * search param, or use the 'useSearchParamState' hook to reduce some boilerplate.
 */
function TaskViewerSheet() {
  const { t } = useTranslation();
  const [{ taskViewer: taskId }, setSearchParams] = useSearchParamState();
  const [closeReasonId, setCloseReasonId] = useState<string | null>(null);
  const [closeDescription, setCloseDescription] = useState("");
  const [patchTask, setPatchTask] = useState<PatchTask>({});
  const [showCloseSheet, setShowCloseSheet] = useState(false);
  const [showMenuSheet, setShowMenuSheet] = useState(false);
  const [addCommentText, setAddCommentText] = useState("");
  const [addCommentImages, setAddCommentImages] = useState<File[]>([]);
  // Note that this just reflects the currently active tab, not control it
  const [historyAndCommentsTab, setHistoryAndCommentsTab] =
    useState<Tab>(DEFAULT_TAB);

  const myUserId = useUID();
  const myFullName = useUser(myUserId).data?.fullName;

  const saveGuardId = useId();

  const { enableSaveGuard, disableSaveGuard } = useSaveGuard(saveGuardId);

  const { data: allCloseReasons } = useAllCloseReasons();
  const {
    mutateAsync: updateTask,
    isPending: isSaving,
    error: updateTaskError,
  } = useUpdateTask();
  const { data: allTaskTypes, isLoading: isLoadingTaskTypes } =
    useAllTaskTypes();
  const { data: originalTask, isFetching: isFetchingOriginalTask } =
    useTask(taskId);
  const { data: logs } = useTaskLogs(taskId);
  const { data: comments } = useTaskComments(taskId);
  const { data: images } = useTaskImages(taskId, originalTask?.image_ids);
  const { data: commentImages } = useTaskCommentsImages(
    historyAndCommentsTab === "comments" ? taskId : null
  );
  const { data: fileUploads } = useTaskFiles(taskId, originalTask?.file_ids);
  const { data: schedule } = useTaskSchedule(taskId);

  const addCommentImagesForImageUploader = useMemo<NewFile[]>(() => {
    return filesToNewFiles(addCommentImages);
  }, [addCommentImages]);

  const {
    ready: addCommentImageUploaderIsReady,
    createUploads: addCommentCreateUploads,
    upload: uploadAddCommentImages,
    error: commentImageUploaderError,
  } = useImageUploader(addCommentImagesForImageUploader);

  const logsTabItems = useMemo(
    () =>
      logs?.map((log: Log) => {
        const translatedParams =
          log.translation_params.map((param) => t(param)) || [];

        return {
          title: log.title,
          content: t(log.translation_key, { params: translatedParams }),
          time: timestampToTimeDiff({
            dateTime: log.created_at,
            t,
          }),
        };
      }) || [],
    [logs, t]
  );

  const commentsTabItems = useMemo<CommentsTabItem[]>(
    () =>
      comments?.map((comment: Comment) => {
        const commentImageUrls = () => {
          if (comment.image_ids.length === 0) {
            return NO_COMMENT_IMAGE_URLS;
          } else if (commentImages === undefined) {
            return LOADING;
          } else {
            const imageUrls = commentImages
              .filter(({ id }) => comment.image_ids.includes(id))
              .map(({ signed_url }) => signed_url);
            if (imageUrls.length !== comment.image_ids.length) {
              // We have created a new comment and are currently refetching images
              return LOADING;
            } else {
              return imageUrls;
            }
          }
        };

        return {
          title: comment.title,
          content: comment.text,
          time: timestampToTimeDiff({
            dateTime: comment.created_at,
            t,
          }),
          imageUrls: commentImageUrls(),
        };
      }) || [],
    [comments, t, commentImages]
  );

  const {
    mutateAsync: createComment,
    isPending: isCreatingComment,
    error: createCommentError,
  } = useCreateComment();

  // Called similarly to the effect above, but before the task has finished being fetched.
  // This prevents showing stale changes applied through the patch task, when
  // fetching a task that is already in the cache.
  const handleTaskIdHasChanged = useEffectEvent(() => {
    setShowCloseSheet(false);
    setShowMenuSheet(false);
    setPatchTask({});
  });

  useEffect(() => {
    handleTaskIdHasChanged();
  }, [taskId]);

  const taskType = useMemo(() => {
    if (!allTaskTypes || !originalTask) {
      return undefined;
    }
    return allTaskTypes.find(({ id }) => id === originalTask.task_type_id);
  }, [originalTask, allTaskTypes]);

  const closeReasonOptions = useMemo(() => {
    if (!allCloseReasons || !taskType) {
      return NO_CLOSE_REASON_OPTIONS;
    } else {
      return allCloseReasons
        .filter(({ id }) => taskType.close_reason_ids.includes(id))
        .map(({ id, label, requires_description: requiresDescription }) => {
          return {
            value: id,
            label: t(label),
            requiresDescription,
          };
        });
    }
  }, [allCloseReasons, t, taskType]);

  const handleCloseClick = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete("taskViewer");
    });
  }, [setSearchParams]);

  const hasUnsavedChanges = Object.keys(patchTask).length > 0;

  const queryClient = useQueryClient();

  const handleSave = useCallback(
    (patchTask: PatchTask) => {
      async function fn() {
        if (taskId) {
          disableSaveGuard();
          await updateTask({
            id: taskId,
            patchTask,
          });
          invalidateTaskQueries(queryClient);
          setPatchTask({});
        }
      }

      fn();
    },
    [disableSaveGuard, queryClient, taskId, updateTask]
  );

  const handleSaveClick = useCallback(() => {
    handleSave(patchTask);
  }, [handleSave, patchTask]);

  const handleCompleteClick = useCallback(() => {
    handleSave({ ...patchTask, state: "completed" });
  }, [handleSave, patchTask]);

  const handlePickUpClick = useCallback(() => {
    handleSave({ ...patchTask, assigned_user_id: myUserId });
  }, [handleSave, myUserId, patchTask]);

  const handleMenuCloseButtonClick = useCallback(() => {
    setShowMenuSheet(false);
  }, []);

  const handleTopMenuButtonClick = useCallback(() => {
    setShowMenuSheet(true);
  }, []);

  const {
    saveGuardedFunction: handleCloseSheetClose,
    enableSaveGuard: enableCloseSheetCloseSaveGuard,
    disableSaveGuard: disableCloseSheetCloseSaveGuard,
  } = useSaveGuardedFunction(
    useCallback(() => {
      setShowCloseSheet(false);
    }, [])
  );

  const { saveGuardedFunction: handleCloseTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        setCloseReasonId(null);
        setCloseDescription("");
        setShowMenuSheet(false);
        setShowCloseSheet(true);
        setPatchTask({});
      }, []),
      saveGuardId
    );

  const { saveGuardedFunction: handleReopenClosedTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          handleSave({
            ...originalTask,
            state: "pending",
            close_reason_id: null,
            close_description: "",
          });
        }
      }, [handleSave, originalTask]),
      saveGuardId
    );

  const { saveGuardedFunction: handleReopenCompletedTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          handleSave({
            ...originalTask,
            state: "pending",
          });
        }
      }, [handleSave, originalTask]),
      saveGuardId
    );

  const { saveGuardedFunction: handleEditTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          setSearchParams((prev) => {
            prev.delete("taskViewer");
            prev.set("taskEditor", String(originalTask.id));
          });
        }
      }, [originalTask, setSearchParams]),
      saveGuardId
    );

  const { saveGuardedFunction: handleDropTaskMenuSheetClick } =
    useSaveGuardedFunction(
      useCallback(() => {
        if (originalTask) {
          setShowMenuSheet(false);
          handleSave({
            ...originalTask,
            assigned_user_id: null,
          });
        }
      }, [handleSave, originalTask]),
      saveGuardId
    );

  const handleCloseSheetCloseTask = useCallback(() => {
    if (originalTask) {
      setShowCloseSheet(false);
      setCloseReasonId(null);
      setCloseDescription("");
      disableCloseSheetCloseSaveGuard();
      handleSave({
        ...originalTask,
        state: "closed",
        close_reason_id: closeReasonId,
        close_description: closeDescription,
      });
    }
  }, [
    closeDescription,
    closeReasonId,
    disableCloseSheetCloseSaveGuard,
    handleSave,
    originalTask,
  ]);

  const blockInput = isSaving || isFetchingOriginalTask || !taskId;

  const menuSheet = useMemo(() => {
    if (!originalTask) {
      return null;
    }
    const items = [
      <MenuSheetItem
        key="edit"
        title={t("editTaskString")}
        disabled={blockInput}
        onClick={handleEditTaskMenuSheetClick}
      />,
    ];
    if (
      originalTask.state === "pending" &&
      myUserId === originalTask.assigned_user_id
    ) {
      items.push(
        <MenuSheetItem
          key="drop"
          title={t("dropString")}
          disabled={blockInput}
          onClick={handleDropTaskMenuSheetClick}
        />
      );
    }
    if (originalTask.state === "pending") {
      items.push(
        <MenuSheetItem
          key="close"
          title={t("closeString")}
          disabled={
            blockInput || closeReasonOptions === NO_CLOSE_REASON_OPTIONS
          }
          onClick={handleCloseTaskMenuSheetClick}
        />
      );
    }
    if (originalTask.state === "closed") {
      items.push(
        <MenuSheetItem
          key="reopen-closed"
          title={t("reopenString")}
          disabled={blockInput}
          onClick={handleReopenClosedTaskMenuSheetClick}
        />
      );
    }
    if (originalTask.state === "completed") {
      items.push(
        <MenuSheetItem
          key="reopen-completed"
          title={t("reopenString")}
          disabled={blockInput}
          onClick={handleReopenCompletedTaskMenuSheetClick}
        />
      );
    }
    return (
      <MenuSheet
        onCloseClick={handleMenuCloseButtonClick}
        visible={showMenuSheet}
      >
        {items}
      </MenuSheet>
    );
  }, [
    originalTask,
    t,
    blockInput,
    handleEditTaskMenuSheetClick,
    myUserId,
    handleMenuCloseButtonClick,
    showMenuSheet,
    handleDropTaskMenuSheetClick,
    closeReasonOptions,
    handleCloseTaskMenuSheetClick,
    handleReopenClosedTaskMenuSheetClick,
    handleReopenCompletedTaskMenuSheetClick,
  ]);

  const handleCloseReasonIdChange = useCallback(
    (newCloseReasonId: string | null) => {
      setCloseReasonId(newCloseReasonId);
      enableCloseSheetCloseSaveGuard();
    },
    [enableCloseSheetCloseSaveGuard]
  );

  const handleCloseDescriptionChange = useCallback(
    (newCloseDescription: string) => {
      setCloseDescription(newCloseDescription);
      enableCloseSheetCloseSaveGuard();
    },
    [enableCloseSheetCloseSaveGuard]
  );

  const closeSheet = useMemo(() => {
    return (
      <CloseSheet
        options={closeReasonOptions}
        closeReasonId={closeReasonId}
        onCloseReasonIdChange={handleCloseReasonIdChange}
        description={closeDescription}
        onDescriptionChange={handleCloseDescriptionChange}
        visible={showCloseSheet}
        onClose={handleCloseSheetClose}
        onCloseTask={handleCloseSheetCloseTask}
      />
    );
  }, [
    closeDescription,
    closeReasonId,
    closeReasonOptions,
    handleCloseDescriptionChange,
    handleCloseReasonIdChange,
    handleCloseSheetClose,
    handleCloseSheetCloseTask,
    showCloseSheet,
  ]);

  const loadingFailed =
    !isFetchingOriginalTask &&
    !isLoadingTaskTypes &&
    (!originalTask || !taskType);

  const taskForFragments = useMemo(() => {
    return !originalTask ? LOADING : merge(originalTask, patchTask);
  }, [originalTask, patchTask]);

  const handleTaskChange = useCallback(
    (part: Record<string, unknown>) => {
      enableSaveGuard();
      setPatchTask((prev) => merge(prev, part));
    },
    [enableSaveGuard]
  );

  const handleMutationResults = useCallback(
    (
      createdCommentMutationResult: PromiseSettledResult<Comment>,
      uploadCommentImagesResult: PromiseSettledResult<void>
    ) => {
      queryClient.invalidateQueries({
        queryKey: getTaskCommentsQueryKey(taskId!),
      });
      if (createdCommentMutationResult.status === "fulfilled") {
        setAddCommentText("");
        setAddCommentImages([]);
      }
      if (uploadCommentImagesResult.status === "rejected") {
        const imageUploadIds =
          createdCommentMutationResult.status === "fulfilled"
            ? createdCommentMutationResult.value?.image_ids
            : "unknown";
        reportMessageToSentry(
          "Image upload failed",
          { uploadIds: imageUploadIds },
          "error"
        );
      }
    },
    [taskId, queryClient]
  );

  const handleAddCommentClick = useCallback(() => {
    async function fn() {
      const [createCommentMutationResult, uploadCommentImagesResult] =
        await Promise.allSettled([
          createComment({
            task_id: taskId!,
            text: addCommentText,
            images: addCommentCreateUploads,
            title: myFullName!,
            creator_user_id: myUserId,
          }),
          uploadAddCommentImages(),
        ]);
      handleMutationResults(
        createCommentMutationResult,
        uploadCommentImagesResult
      );
    }

    fn();
  }, [
    createComment,
    taskId,
    addCommentText,
    addCommentCreateUploads,
    myFullName,
    myUserId,
    uploadAddCommentImages,
    handleMutationResults,
  ]);

  const handleAddCommentTextChange = useCallback(
    (text: string) => {
      if (addCommentText.length === 0 && text.startsWith(" ")) {
        return;
      }
      setAddCommentText(text);
    },
    [addCommentText]
  );

  const viewerFragmentCommonProps: ViewerFragmentCommonProps = useMemo(() => {
    return {
      blockInput,
      task: taskForFragments,
      onTaskChange: handleTaskChange,
    };
  }, [blockInput, handleTaskChange, taskForFragments]);

  const viewerTopFragmentCommonProps =
    useMemo<ViewerTopFragmentCommonProps>(() => {
      return { task: taskForFragments };
    }, [taskForFragments]);

  const viewerTopFragment = useMemo(() => {
    if (!taskType) {
      return null;
    }
    return (
      <ViewerTopFragment
        config={taskType.viewer_top_fragment}
        commonProps={viewerTopFragmentCommonProps}
      />
    );
  }, [taskType, viewerTopFragmentCommonProps]);

  const recurrenceText = useMemo(() => {
    if (!schedule) {
      return null;
    }

    if (schedule.recurrence.period === "daily") {
      return t("repeatsDailyString");
    } else if (schedule.recurrence.period === "weekly") {
      return t("repeatsWeeklyString");
    } else if (schedule.recurrence.period === "monthly") {
      return t("repeatsMonthlyString");
    } else {
      return null;
    }
  }, [schedule, t]);

  const header = useMemo(() => {
    if (loadingFailed) {
      return <FixaModalHeader closeBtnClick={handleCloseClick} />;
    } else if (!taskType) {
      return <LoadingViewerTop onCloseButtonClick={handleCloseClick} />;
    } else {
      return (
        <TaskViewerHeader
          task={taskForFragments}
          onCloseButtonClick={handleCloseClick}
          onMenuButtonClick={handleTopMenuButtonClick}
          topFragment={viewerTopFragment}
          recurrenceText={recurrenceText}
        />
      );
    }
  }, [
    loadingFailed,
    taskType,
    handleCloseClick,
    taskForFragments,
    handleTopMenuButtonClick,
    viewerTopFragment,
    recurrenceText,
  ]);

  const footer = useMemo(() => {
    if (loadingFailed) {
      return null;
    } else {
      function createFooterContent() {
        if (!originalTask || isSaving || isFetchingOriginalTask) {
          return <FixaButton loading type="primary" />;
        } else if (originalTask.state === "pending") {
          if (originalTask.assigned_user_id === myUserId) {
            if (hasUnsavedChanges) {
              return (
                <>
                  <FixaButton
                    text={t("saveString")}
                    onClick={handleSaveClick}
                    type="primary"
                    disabled={blockInput}
                  />
                  <FixaButton
                    text={t("saveAndCompleteString")}
                    onClick={handleCompleteClick}
                    disabled={blockInput}
                  />
                </>
              );
            } else {
              return (
                <FixaButton
                  text={t("completeString")}
                  onClick={handleCompleteClick}
                  type="primary"
                  disabled={blockInput}
                />
              );
            }
          } else {
            if (hasUnsavedChanges) {
              return (
                <>
                  <FixaButton
                    text={t("saveString")}
                    onClick={handleSaveClick}
                    type="primary"
                    disabled={blockInput}
                  />
                  <FixaButton
                    text={t("saveAndPickUpString")}
                    onClick={handlePickUpClick}
                    disabled={blockInput}
                  />
                </>
              );
            } else {
              return (
                <FixaButton
                  text={t("pickUpString")}
                  onClick={handlePickUpClick}
                  type="primary"
                  disabled={blockInput}
                />
              );
            }
          }
        } else {
          if (hasUnsavedChanges) {
            return (
              <FixaButton
                text={t("saveString")}
                onClick={handleSaveClick}
                type="primary"
                disabled={blockInput}
              />
            );
          } else {
            return null;
          }
        }
      }

      return <FixaModalFooter>{createFooterContent()}</FixaModalFooter>;
    }
  }, [
    loadingFailed,
    originalTask,
    isSaving,
    isFetchingOriginalTask,
    myUserId,
    hasUnsavedChanges,
    t,
    handleSaveClick,
    blockInput,
    handleCompleteClick,
    handlePickUpClick,
  ]);

  const startElement = useMemo(() => {
    if (originalTask?.start) {
      return (
        <TitledText title={t("scheduledForString")} text={originalTask.start} />
      );
    } else {
      return null;
    }
  }, [originalTask, t]);

  const imagesElement = useMemo(() => {
    if (images !== undefined && images.length > 0) {
      return <Images urls={images.map(({ file }) => file.url)} />;
    } else if (
      originalTask !== undefined &&
      originalTask.image_ids.length > 0
    ) {
      return <Images urls={LOADING} />;
    } else {
      return null;
    }
  }, [images, originalTask]);

  const fileItems = useMemo<FileItem[]>(() => {
    if (fileUploads === undefined) {
      return NO_FILE_ITEMS;
    } else {
      return fileUploads.map(({ file }) => {
        return {
          name: file.name,
          size: formatFileSize(file.size),
          onDownload: async () => {
            try {
              // Should probably show some prompt here as well
              downloadFileFromUrl(file.url, file.name);
            } catch (e) {
              reportMessageToSentry(
                `Failed to download task file from upload '${file.id}'`,
                { error: e },
                "error"
              );
            }
          },
        };
      });
    }
  }, [fileUploads]);

  const content = useMemo(() => {
    if (loadingFailed) {
      return (
        <NotFound
          title={t("sorryTaskCouldNotBeFoundString")}
          message={t("thisTaskDoesNotExistString")}
        />
      );
    } else if (!taskType) {
      return header;
    } else {
      return (
        <div>
          {header}
          <div className={styles["contentWrapper"]}>
            {imagesElement}
            {taskType.viewer_fragments.map((config, i) => (
              <ViewerFragment
                key={i}
                config={config}
                commonProps={viewerFragmentCommonProps}
              />
            ))}
            <UserNameViewerFragment
              configProps={{
                title: t("createdByString"),
                field: "creator_user_id",
              }}
              commonProps={viewerFragmentCommonProps}
            />
            <UserAndTeamViewerFragment
              configProps={{
                title: t("assignedToTaskFilterString"),
                userIdField: "assigned_user_id",
                teamIdField: "assigned_team_id",
              }}
              commonProps={viewerFragmentCommonProps}
            />
            {startElement}
            <Files items={fileItems} />
            <LogsAndComments
              onTabChanged={setHistoryAndCommentsTab}
              logsTabItems={logsTabItems}
              commentsTabItems={commentsTabItems}
              addCommentText={addCommentText}
              addCommentImages={addCommentImages}
              disableAllAddCommentInputs={isCreatingComment}
              disableAddCommentButton={!addCommentImageUploaderIsReady}
              onAddCommentTextChange={handleAddCommentTextChange}
              onAddCommentImagesChange={setAddCommentImages}
              onAddCommentClick={handleAddCommentClick}
            />
          </div>
        </div>
      );
    }
  }, [
    loadingFailed,
    taskType,
    t,
    header,
    imagesElement,
    viewerFragmentCommonProps,
    startElement,
    fileItems,
    logsTabItems,
    commentsTabItems,
    addCommentText,
    addCommentImages,
    isCreatingComment,
    addCommentImageUploaderIsReady,
    handleAddCommentTextChange,
    handleAddCommentClick,
  ]);

  const handleRefreshClick = useCallback(() => {
    disableSaveGuard();
    setTimeout(() => {
      window.location.reload();
    }, 0);
  }, [disableSaveGuard]);

  const showSomethingWentWrongPrompt: boolean =
    !!updateTaskError || !!createCommentError || !!commentImageUploaderError;

  return (
    <>
      <FixaModal visible={!!taskId} handleCloseBtn={handleCloseClick}>
        <FixaSheets
          className={loadingFailed ? "" : styles["greyBackground"]}
          header={null}
          footer={footer}
          fullSize
        >
          {content}
        </FixaSheets>
      </FixaModal>
      {menuSheet}
      {closeSheet}
      {showSomethingWentWrongPrompt && (
        <SomethingWentWrongPrompt onRefreshClick={handleRefreshClick} />
      )}
    </>
  );
}

type DateProps = {
  dateTime: string;
  t: TFunction;
};

function timestampToTimeDiff({ dateTime, t }: DateProps): string {
  if (!dateTime) return "";
  const localDate = new Date(dateTime);
  const currentTime = dayjs();
  const timeDifferenceMinutes = currentTime.diff(localDate, "minutes");
  const timeDifferenceHours = currentTime.diff(localDate, "hours");
  const timeDifferenceDays = currentTime.diff(localDate, "days");

  const getTimeString = (
    difference: number,
    singularKey: string,
    pluralKey: string
  ) =>
    `${difference} ${difference === 1 ? t(singularKey) : t(pluralKey)} ${t(
      "agoString"
    )}`;

  if (timeDifferenceMinutes === 0) return t("justNowString");
  if (timeDifferenceMinutes < 60)
    return getTimeString(
      timeDifferenceMinutes,
      "minuteString",
      "minutesString"
    );
  if (timeDifferenceHours < 24)
    return getTimeString(timeDifferenceHours, "hourString", "hoursString");
  if (timeDifferenceDays <= 5)
    return getTimeString(timeDifferenceDays, "dayString", "daysString");

  return localDate.toISOString().split("T")[0]!;
}

export default TaskViewerSheet;
